import axios from "axios";
import { App } from "antd";
import { TOKEN } from "../common/constants";


const http = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN);
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    config.headers["Time-Zone"] = timezone;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors globally with toast messages
http.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const { message } = App.useApp();
    console.error("API Error:", error);

    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const errorData = error.response.data;

      switch (status) {
        case 400:
          // Bad Request
          message.error(errorData?.message || "Invalid request. Please check your input.");
          break;

        case 401:
          // Unauthorized - clear auth and redirect to login
          message.error("Your session has expired. Please login again.");
          localStorage.clear();
          
          if (window.location.pathname !== "/login") {
            setTimeout(() => {
              window.location.href = "/login";
            }, 1500);
          }
          break;

        case 403:
          // Forbidden
          message.error("You don't have permission to perform this action.");
          break;

        case 404:
          // Not Found
          message.error(errorData?.message || "The requested resource was not found.");
          break;

        case 422:
          // Validation Error
          if (errorData?.message) {
            // Clean up validation error messages
            const cleanMessage = errorData.message
              .replace(/[{}[\]'"]/g, '') // Remove brackets and quotes
              .replace(/ErrorDetail\(string=/g, '') // Remove ErrorDetail wrapper
              .replace(/code=invalid_choice\)/g, '') // Remove code info
              .split(',')[0]; // Take first error if multiple
            
            message.error(cleanMessage || "Validation failed. Please check your input.");
          } else if (errorData?.errors) {
            // Handle validation errors object
            const firstError = Object.values(errorData.errors)[0];
            message.error(Array.isArray(firstError) ? firstError[0] : firstError);
          } else {
            message.error("Validation failed. Please check your input.");
          }
          break;

        case 503:
          // Service Unavailable
          message.error("Service temporarily unavailable. Please try again later.");
          break;

        default:
          // Other errors
          message.error(errorData?.message || `Error: ${status}. Something went wrong.`);
      }
    } else if (error.request) {
      // Request made but no response received (network error)
      message.error("No response from server. Please check your internet connection.");
    } else {
      // Something else happened
      message.error(error.message || "An unexpected error occurred.");
    }

    return Promise.reject(error);
  }
);

export default http;